import { type FC, useRef, useState } from 'react'
import PinField from 'react-pin-field'
import Link from 'next/link'

import { Button, Stack, Typography } from '@mui/material'
import { EmailOpenOutlinedIcon } from '@core/components/icons'
import { AlertDialog, CustomInputHelperText } from '@core/components/ui'
import { BackdropLoader } from '@core/components/ui/BackdropLoader/BackdropLoader'
import { useLayout } from '@core/contexts/layout'
import { colors } from '@core/helpers'
import { NUMERIC_REGEX, SERVER_ERROR_MESSAGE } from '@core/lib/utils'
import useMiniAlertStore from '@core/stores/miniAlert/useMiniAlertStore'
import { sendResetPasswordEmail, validateCodeAndChangePass } from '@services/login'

interface Props {
  documentNumber: string
  documentType: number
  email: string
  newPassword: string
  token: string
  onContinue: () => void
}

export const ConfirmMessageCode: FC<Props> = ({
  email,
  token: initialToken,
  onContinue,
  documentNumber,
  documentType,
  newPassword
}) => {
  const { isMobile } = useLayout()
  const { openAlert } = useMiniAlertStore()
  const token = useRef(initialToken)
  const pinRef = useRef<HTMLInputElement[] | null>(null)

  const [resendingMessage, setResendingMessage] = useState(false)
  const [openSuccessResendDialog, setOpenSuccessResendDialog] = useState(false)
  const [isValidatingCode, setIsValidatingCode] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onComplete = async (code: string) => {
    setIsValidatingCode(true)

    try {
      await validateCodeAndChangePass({
        idToken: token.current,
        code,
        newPasswordHash: newPassword
      })
      onContinue()
    } catch (error: any) {
      console.error(error)
      resetInputs(pinRef.current)
      focusElement(pinRef.current, 0)

      const errorMessage = error.message as string

      if (errorMessage.startsWith('Esta sesión expiró')) {
        setError('Este código de verificación expiró, por favor solicite otro')

        return
      }
      if (errorMessage.startsWith('Código de verificación errado')) {
        setError('Código de verificación errado')

        return
      }
      if (errorMessage.startsWith('Esta sesión ya fue utilizada')) {
        setError('Este código de verificación ya ha sido utilizado')

        return
      }

      setError(SERVER_ERROR_MESSAGE)
    } finally {
      setIsValidatingCode(false)
    }
  }

  const onResendMessage = async () => {
    setOpenSuccessResendDialog(true)
    setResendingMessage(true)
    try {
      const res = await sendResetPasswordEmail({ documentNumber, documentType }, openAlert)
      if (!res) throw new Error('Fallo enviando mensaje al correo')

      setOpenSuccessResendDialog(true)
      token.current = res.id
    } catch (error: any) {
      const errorMessage = error.message as string
      if (errorMessage.startsWith('El usuario no se encuentra registrado.')) {
        openAlert(errorMessage)

        return
      }
      openAlert('Error interno enviando el mensaje')
    } finally {
      setResendingMessage(false)
    }
  }

  return (
    <Stack width='100%' alignItems='center'>
      <Stack
        height={isMobile ? undefined : '80vh'}
        justifyContent={isMobile ? undefined : 'center'}
        maxWidth={isMobile ? undefined : '39.2rem'}
        alignItems='center'
      >
        <Stack component='section' mb={isMobile ? '4.1rem' : '2.4rem'}>
          <Stack alignItems='center' mb={isMobile ? '1.6rem' : '2.4rem'}>
            <EmailOpenOutlinedIcon color={colors.primary_default} size={56} />
          </Stack>
          <Typography
            textAlign='center'
            variant={isMobile ? 'd3' : 'h1_bold'}
            color='secondary'
            mb='1.6rem'
          >
            Revisa tu correo
          </Typography>
          <Typography
            variant={isMobile ? 'body3_medium' : 'body2_medium'}
            color={colors.text_title}
            textAlign='center'
            mb='4rem'
          >
            Te hemos enviado un enlace a tu correo {email} con un código para que puedas cambiar tu
            contraseña.
          </Typography>

          <Stack mb='2.4rem' spacing='2.4rem' justifyContent='center' alignItems='center'>
            <Stack
              mb='2.4rem'
              direction='row'
              spacing='2.4rem'
              justifyContent='center'
              alignItems='center'
            >
              <PinField
                validate={NUMERIC_REGEX}
                className='pin-code'
                ref={pinRef}
                id='pin-input'
                length={4}
                onChange={() => setError(null)}
                onComplete={onComplete}
              />
            </Stack>
            {error && <CustomInputHelperText text={error} state='error' htmlFor='pin-input' />}
          </Stack>

          <Typography
            variant={isMobile ? 'body3_medium' : 'body2_medium'}
            color={colors.text_title}
            textAlign='center'
          >
            ¿No recibiste el correo?
          </Typography>
        </Stack>

        <Stack
          mb='2.4rem'
          component='section'
          alignItems='center'
          gap={isMobile ? '1.6rem' : '0.8rem'}
        >
          <Button
            onClick={onResendMessage}
            sx={{ width: isMobile ? '23.4rem' : '19rem' }}
            variant='outlined'
          >
            Reenviar correo
          </Button>
          <Button
            LinkComponent={Link}
            href='/'
            sx={{ width: isMobile ? '23.4rem' : '19rem' }}
            variant='text'
          >
            Ir al inicio
          </Button>
        </Stack>

        <Typography
          component='p'
          fontSize='1.6rem'
          textAlign='center'
          fontWeight='700'
          lineHeight='160%'
          color='secondary'
          letterSpacing='-0.16px'
          maxWidth='36.8rem'
        >
          Si no reconoce su correo electrónico o celular, contactarse al{' '}
          <a href='tel:(01) 7154600'>(01) 7154600</a> para actualizar sus datos.
        </Typography>
      </Stack>

      <BackdropLoader
        loading={isValidatingCode || resendingMessage}
        title={isValidatingCode ? 'Validando código...' : 'Reenviando mensaje...'}
      />

      <AlertDialog
        open={openSuccessResendDialog}
        setOpen={setOpenSuccessResendDialog}
        title='Mensaje reenviado'
        type='success'
        description='Se ha reenviado el mensaje al correo electrónico asociado'
        onClose={() => setOpenSuccessResendDialog(false)}
        onContinue={() => setOpenSuccessResendDialog(false)}
        footerButtonText='Cerrar'
      />
    </Stack>
  )
}
const resetInputs = (ref: HTMLInputElement[] | null) => {
  if (!ref) return
  ref.forEach(input => (input.value = ''))
}

const focusElement = (ref: HTMLInputElement[] | null, num: number) => {
  if (!ref) return
  ref[num].focus()
}
